import { useEffect } from 'react'

import type { Route } from '../types/routeTypes'
import { logEvent } from '../utils/analytics'

import { useRouter } from './useRouter'

/**
 * Validates the user's progress through a multi-step form by checking if they have completed the previous steps.
 */
export const useStepValidation = (
  routes: Route<string>[],
  data: Record<string, any>,
  promoCode?: string
) => {
  const { isReady, replace, resolvedPathname } = useRouter()

  useEffect(() => {
    // Run this function after routes and router is ready
    if (!routes || !isReady) {
      return
    }
    // Get the stored session data
    const currentStepIndex = routes.findIndex(
      (route) => route.path === resolvedPathname
    )

    if (!routes[currentStepIndex]?.isValidatedStep) return

    // Steps that are able to be validated
    const stepRoutes = routes.slice(0, currentStepIndex).filter((route) => {
      if (route.stateKey) {
        if (route.condition) {
          return route.condition(data, { promoCode })
        } else {
          return true
        }
      }

      return false
    })
    const keysToValidate = stepRoutes.map((route) => route.stateKey) as string[]

    // Get the keys of the stored data and the keys of the completed steps
    const storeKeys = Object.keys(data)

    // If the user has not completed the previous steps
    // redirect them to last missing step
    if (storeKeys.length > 0) {
      for (let i = 0; i < keysToValidate.length; i++) {
        if (!storeKeys.includes(keysToValidate[i])) {
          const missedStep = routes.find(
            (route) => route.stateKey === keysToValidate[i]
          ) as Route<string>

          logEvent('onboarding_step_validation_redirect', {
            current_step: resolvedPathname,
            missed_step: missedStep?.stateKey,
          })

          void replace(missedStep?.path)
          break
        }
      }
    }

    // Check if user has no stored data and step is after gender step
    if (currentStepIndex > 1 && storeKeys.length === 0) {
      void replace('/')
    }
  }, [isReady, promoCode, resolvedPathname, replace, routes, data])
}
